import { useEffect, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCopy } from "@fortawesome/free-solid-svg-icons";

import Button from "components/Button";
import Loading from "components/Loading";

import councilPortalService from "api/councilPortalService";
import pluginService from "api/pluginService";
import { ECouncilVotingType } from "dto/Council/ICouncil";
import { ISaveCouncilCameraRequest } from "dto/Council/ICouncilPortal";
import { ENotificationType, notification } from "utils/notification";

import CamerasSection from "./CamerasSection";
import { ICameraDraft, toCameraDrafts } from "./cameraDrafts";
import { decodeText } from "./decodeText";
import styles from "./styles.module.scss";

// Biuro rady → „Portal radnego". Odpowiednik ustawień urzędu (Office) i CouncilSettings
// z Magistratu, okrojony do tego, czego potrzebuje portal webowy.
//
// Kluczowe pole to adres portalu (slug). W Magistracie nie istniało — każdy urząd miał
// własną instancję portalu z własną bazą. Tutaj baza jest wspólna, więc portal poznaje
// urząd wyłącznie po tym fragmencie adresu.

const PORTAL_SITE = import.meta.env.VITE_APP_PORTAL_SITE ?? "";

const SLUG_PATTERN = /^[a-z0-9][a-z0-9-]{1,62}[a-z0-9]$/;

const errorText = (e: unknown, fallback: string) =>
  (e as { response?: { data?: { Message?: string } } })?.response?.data?.Message ??
  fallback;

const PortalSettingsView = () => {
  const settingsQuery = councilPortalService.useGetPortalSettings();
  const { mutate: save, isLoading: saving } =
    councilPortalService.useSavePortalSettings();

  const [slug, setSlug] = useState("");
  const [officeName, setOfficeName] = useState("");
  const [portalHeader, setPortalHeader] = useState("");
  const [total, setTotal] = useState("");
  const [absoluteType, setAbsoluteType] = useState<ECouncilVotingType>(
    ECouncilVotingType.AbsoluteVer1,
  );
  const [streaming, setStreaming] = useState(false);
  const [channel, setChannel] = useState("");
  const [enabled, setEnabled] = useState(false);

  // Transmisja: OBS Studio, napisy OSD, kamery PTZ.
  const [osdPath, setOsdPath] = useState("");
  const [obsUseLocal, setObsUseLocal] = useState(true);
  const [obsAddress, setObsAddress] = useState("");
  const [obsPassword, setObsPassword] = useState("");
  const [removeObsPassword, setRemoveObsPassword] = useState(false);
  const [obsDefaultScene, setObsDefaultScene] = useState("");
  const [obsVotingScene, setObsVotingScene] = useState("");
  const [camerasEnabled, setCamerasEnabled] = useState(false);
  const [cameras, setCameras] = useState<ICameraDraft[]>([]);

  const loaded = settingsQuery.data?.data;

  useEffect(() => {
    if (!loaded) return;

    setSlug(decodeText(loaded.Slug));
    setOfficeName(decodeText(loaded.OfficeName));
    setPortalHeader(decodeText(loaded.PortalHeader));
    setTotal(loaded.TotalCouncilorsCount ? String(loaded.TotalCouncilorsCount) : "");
    setAbsoluteType(loaded.AbsoluteVotingType ?? ECouncilVotingType.AbsoluteVer1);
    setStreaming(loaded.SupportsStreaming);
    setChannel(decodeText(loaded.YouTubeChannel));
    setEnabled(loaded.IsEnabled);

    setOsdPath(decodeText(loaded.ObsTextFilePath));
    setObsUseLocal(loaded.ObsUseLocal);
    setObsAddress(decodeText(loaded.ObsAddress));
    setObsPassword("");
    setRemoveObsPassword(false);
    setObsDefaultScene(decodeText(loaded.ObsDefaultScene));
    setObsVotingScene(decodeText(loaded.ObsVotingScene));
    setCamerasEnabled(loaded.EnableDahuaIntegration);
    setCameras(toCameraDrafts(loaded.Cameras ?? []));
  }, [loaded]);

  const slugValid = SLUG_PATTERN.test(slug.trim());
  const totalValue = total.trim() ? Number(total.trim()) : null;
  const totalValid = totalValue === null || (Number.isInteger(totalValue) && totalValue > 0);

  const portalUrl = slugValid && PORTAL_SITE ? `${PORTAL_SITE}/${slug.trim()}` : "";

  // Kamera bez adresu i widok bez nazwy odrzuci backend — sprawdzamy to tutaj,
  // żeby nie zgubić całego formularza na komunikacie z serwera.
  //
  // Tylko przy włączonej integracji: przy wyłączonej sekcja kamer w ogóle nie jest
  // widoczna, a niedokończona konfiguracja blokowałaby zapis bez żadnego komunikatu
  // — formularz stawał się ślepym zaułkiem.
  const camerasValid =
    !camerasEnabled ||
    cameras.every(
      (c) =>
        !!c.Address.trim() &&
        c.Presets.every((p) => !!p.Name.trim() && Number(p.Number || 0) >= 1),
    );

  const toCameraRequests = (): ISaveCouncilCameraRequest[] =>
    cameras.map((c) => ({
      Id: c.Id ?? null,
      Address: c.Address.trim(),
      Login: c.Login.trim() || null,
      Password: c.Password || null,
      RemovePassword: c.RemovePassword,
      Scene: c.Scene.trim() || null,
      Presets: c.Presets.map((p) => ({
        Id: p.Id ?? null,
        Name: p.Name.trim(),
        Number: Number(p.Number || 0),
      })),
    }));

  const doSave = () => {
    if (!slugValid || !totalValid || !camerasValid) return;

    save(
      {
        Slug: slug.trim(),
        OfficeName: officeName.trim() || null,
        PortalHeader: portalHeader.trim() || null,
        TotalCouncilorsCount: totalValue,
        AbsoluteVotingType: absoluteType,
        SupportsStreaming: streaming,
        YouTubeChannel: channel.trim() || null,
        ObsTextFilePath: osdPath.trim() || null,
        ObsUseLocal: obsUseLocal,
        ObsAddress: obsAddress.trim() || null,
        ObsPassword: obsPassword || null,
        RemoveObsPassword: removeObsPassword,
        ObsDefaultScene: obsDefaultScene.trim() || null,
        ObsVotingScene: obsVotingScene.trim() || null,
        EnableDahuaIntegration: camerasEnabled,
        Cameras: toCameraRequests(),
        IsEnabled: enabled,
      },
      {
        onSuccess: () => {
          settingsQuery.refetch();

          // Wtyczka trzyma konfigurację transmisji w pamięci przez kilka minut —
          // bez tego zmiana adresu OBS albo presetu zadziałałaby dopiero po chwili.
          // Brak wtyczki na tym stanowisku jest normalny i nie jest błędem zapisu.
          pluginService.reloadCouncilStream().catch(() => undefined);
          notification({
            type: ENotificationType.SUCCESS,
            title: "Zapisano",
            text: "Ustawienia portalu radnego zostały zapisane.",
          });
        },
        onError: (e) =>
          notification({
            type: ENotificationType.ERROR,
            title: "Błąd",
            text: errorText(e, "Nie udało się zapisać ustawień."),
          }),
      },
    );
  };

  if (settingsQuery.isLoading) {
    return <Loading />;
  }

  const activeCouncilors = loaded?.ActiveCouncilorsCount ?? 0;
  const mismatch = totalValue !== null && totalValue !== activeCouncilors;

  return (
    <div className={styles.panel}>
      <div className={styles.settingsWrap}>
        <div className={styles.settingsSection}>
          <div className={styles.settingsSectionTitle}>Adres portalu</div>

          <div className={styles.field}>
            <label className={styles.fieldLabel}>
              Identyfikator urzędu w adresie
            </label>
            <input
              className={styles.textInput}
              value={slug}
              onChange={(e) => setSlug(e.target.value.toLowerCase())}
              placeholder="np. czersk"
            />
            {!!slug && !slugValid && (
              <span className={styles.fieldError}>
                Dozwolone małe litery, cyfry i myślnik (od 3 do 64 znaków).
              </span>
            )}
          </div>

          {portalUrl && (
            <div className={styles.portalUrlRow}>
              <span className={styles.portalUrl}>{portalUrl}</span>
              <button
                type="button"
                className={styles.toolBtn}
                title="Kopiuj adres"
                onClick={() => {
                  navigator.clipboard?.writeText(portalUrl);
                  notification({
                    type: ENotificationType.SUCCESS,
                    title: "Skopiowano",
                    text: "Adres portalu jest w schowku.",
                  });
                }}
              >
                <FontAwesomeIcon icon={faCopy} />
              </button>
            </div>
          )}

          <label className={styles.radioOption}>
            <input
              type="checkbox"
              checked={enabled}
              onChange={(e) => setEnabled(e.target.checked)}
            />
            Portal włączony
          </label>
          {!enabled && (
            <span className={styles.fieldLabel}>
              Przy wyłączonym portalu adres zwraca „nie znaleziono” — także radnym.
            </span>
          )}
        </div>

        <div className={styles.settingsSection}>
          <div className={styles.settingsSectionTitle}>Prezentacja</div>

          <div className={styles.field}>
            <label className={styles.fieldLabel}>Nazwa urzędu</label>
            <input
              className={styles.textInput}
              value={officeName}
              onChange={(e) => setOfficeName(e.target.value)}
              placeholder="np. Urząd Miejski w Czersku"
            />
          </div>

          <div className={styles.field}>
            <label className={styles.fieldLabel}>Nagłówek portalu</label>
            <input
              className={styles.textInput}
              value={portalHeader}
              onChange={(e) => setPortalHeader(e.target.value)}
              placeholder="np. Rada Miejska w Czersku"
            />
          </div>

          <label className={styles.radioOption}>
            <input
              type="checkbox"
              checked={streaming}
              onChange={(e) => setStreaming(e.target.checked)}
            />
            Transmisja obrad
          </label>

          {streaming && (
            <div className={styles.field}>
              <label className={styles.fieldLabel}>Kanał YouTube</label>
              <input
                className={styles.textInput}
                value={channel}
                onChange={(e) => setChannel(e.target.value)}
                placeholder="identyfikator kanału"
              />
            </div>
          )}
        </div>

        {streaming && (
          <div className={styles.settingsSection}>
            <div className={styles.settingsSectionTitle}>OBS Studio</div>

            <span className={styles.fieldLabel}>
              Sterowanie OBS i zapis napisów wykonuje wtyczka Nota na stanowisku
              realizatora — przeglądarka nie połączy się z OBS ani nie zapisze plików
              w sieci. Wtyczka musi być uruchomiona na komputerze, z którego prowadzi
              się obrady.
            </span>

            <div className={styles.field}>
              <label className={styles.fieldLabel}>Gdzie działa OBS Studio</label>
              <div className={styles.radioRow}>
                <label className={styles.radioOption}>
                  <input
                    type="radio"
                    checked={obsUseLocal}
                    onChange={() => setObsUseLocal(true)}
                  />
                  Ten sam komputer
                </label>
                <label className={styles.radioOption}>
                  <input
                    type="radio"
                    checked={!obsUseLocal}
                    onChange={() => setObsUseLocal(false)}
                  />
                  Komputer zdalny
                </label>
              </div>
            </div>

            {!obsUseLocal && (
              <div className={styles.field}>
                <label className={styles.fieldLabel}>Adres serwera obs-websocket</label>
                <input
                  className={styles.textInput}
                  value={obsAddress}
                  onChange={(e) => setObsAddress(e.target.value)}
                  placeholder="np. ws://192.168.2.41:4455"
                />
              </div>
            )}

            <div className={styles.field}>
              <label className={styles.fieldLabel}>Hasło obs-websocket</label>
              <input
                className={styles.textInput}
                type="password"
                autoComplete="new-password"
                value={obsPassword}
                disabled={removeObsPassword}
                onChange={(e) => setObsPassword(e.target.value)}
                placeholder={loaded?.HasObsPassword ? "bez zmiany" : "puste = bez hasła"}
              />
              {loaded?.HasObsPassword && (
                <label className={styles.radioOption}>
                  <input
                    type="checkbox"
                    checked={removeObsPassword}
                    onChange={(e) => {
                      setRemoveObsPassword(e.target.checked);
                      setObsPassword("");
                    }}
                  />
                  Usuń zapisane hasło
                </label>
              )}
              <span className={styles.fieldLabel}>
                Wymagane jest OBS Studio w wersji 28 lub nowszej — serwer WebSocket
                włącza się w menu Narzędzia → Ustawienia serwera WebSocket. Starsza
                wtyczka obs-websocket 4.x nie jest obsługiwana.
              </span>
            </div>

            <div className={styles.fieldRow}>
              <div className={styles.field}>
                <label className={styles.fieldLabel}>Scena domyślna</label>
                <input
                  className={styles.textInput}
                  value={obsDefaultScene}
                  onChange={(e) => setObsDefaultScene(e.target.value)}
                  placeholder="nazwa sceny w OBS"
                />
              </div>

              <div className={styles.field}>
                <label className={styles.fieldLabel}>Scena głosowania</label>
                <input
                  className={styles.textInput}
                  value={obsVotingScene}
                  onChange={(e) => setObsVotingScene(e.target.value)}
                  placeholder="nazwa sceny w OBS"
                />
              </div>
            </div>

            <span className={styles.fieldLabel}>
              Na czas głosowania OBS przechodzi na scenę głosowania i wraca na domyślną
              po jego zamknięciu. Nazwy scen łatwiej wskazać w panelu „Prowadzenie
              obrad”, gdy wtyczka jest połączona z OBS — tam widać ich listę.
            </span>

            <div className={styles.field}>
              <label className={styles.fieldLabel}>
                Katalog współdzielony z napisami OBS
              </label>
              <input
                className={styles.textInput}
                value={osdPath}
                onChange={(e) => setOsdPath(e.target.value)}
                placeholder="np. \\serwer\obs\napisy"
              />
              <span className={styles.fieldLabel}>
                Wtyczka zapisuje w nim trzy pliki tekstowe: pasek.txt (pasek
                informacyjny), tytul.txt (tytuł sesji) i mowca.txt (imię i funkcja
                mówcy). W OBS podpina się je jako źródła „Tekst” z odczytem z pliku.
                Puste pole wyłącza napisy.
              </span>
            </div>
          </div>
        )}

        <div className={styles.settingsSection}>
          <div className={styles.settingsSectionTitle}>Kamery PTZ</div>

          <label className={styles.radioOption}>
            <input
              type="checkbox"
              checked={camerasEnabled}
              onChange={(e) => setCamerasEnabled(e.target.checked)}
            />
            Sterowanie kamerami Dahua
          </label>

          {camerasEnabled && (
            <>
              <span className={styles.fieldLabel}>
                Podczas obrad prowadzący wskazuje widok („Mównica”, „Widok radnych”,
                „Widok sali”), a kamera najeżdża na zaprogramowany w niej preset.
                Kamery są w sieci urzędu, więc wywołuje je wtyczka Nota, a nie serwer.
              </span>

              <CamerasSection cameras={cameras} onChange={setCameras} />

              {!camerasValid && (
                <span className={styles.fieldError}>
                  Każda kamera musi mieć adres, a każdy widok — nazwę i numer presetu
                  z zakresu 1-255.
                </span>
              )}
            </>
          )}
        </div>

        <div className={styles.settingsSection}>
          <div className={styles.settingsSectionTitle}>Głosowania</div>

          <div className={styles.field}>
            <label className={styles.fieldLabel}>Ustawowy skład rady</label>
            <input
              className={styles.textInput}
              value={total}
              inputMode="numeric"
              onChange={(e) => setTotal(e.target.value.replace(/[^0-9]/g, ""))}
              placeholder="np. 15"
            />
            {!totalValid && (
              <span className={styles.fieldError}>Podaj liczbę większą od zera.</span>
            )}
            <span className={styles.fieldLabel}>
              Bez tej liczby nie da się zamknąć głosowania — od niej liczą się progi
              większości bezwzględnej ustawowego składu i kwalifikowanej.
            </span>
            {mismatch && (
              <span className={styles.warningInline}>
                Aktywnych kont radnych: {activeCouncilors}.
              </span>
            )}
          </div>

          <div className={styles.field}>
            <label className={styles.fieldLabel}>Wariant większości bezwzględnej</label>
            <select
              className={styles.textInput}
              value={absoluteType}
              onChange={(e) =>
                setAbsoluteType(Number(e.target.value) as ECouncilVotingType)
              }
            >
              <option value={ECouncilVotingType.AbsoluteVer1}>
                Więcej niż połowa głosujących
              </option>
              <option value={ECouncilVotingType.AbsoluteVer2}>
                Więcej niż połowa, przy nieparzystej liczbie zaokrąglana w górę
              </option>
            </select>
            <span className={styles.fieldLabel}>
              Wariant jest utrwalany w chwili otwarcia głosowania — późniejsza zmiana nie
              wpływa na głosowania już przeprowadzone.
            </span>
          </div>
        </div>

        <div className={styles.settingsFooter}>
          <Button
            text="Zapisz ustawienia"
            loading={saving}
            disabled={!slugValid || !totalValid || !camerasValid}
            onClick={doSave}
          />
        </div>
      </div>
    </div>
  );
};

export default PortalSettingsView;
